import React, { useState, useEffect } from 'react';
import { calendarService } from '../services/api';
import { PlusIcon } from '../assets/icons';
import BlockScheduleDialog from './BlockScheduleDialog';
import BlockScheduleList from './BlockScheduleList';
import { useLanguage } from '../contexts/LanguageContext';

const HolidaySection = () => {
  const { t } = useLanguage();
  const [actionLoading, setActionLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [allBlockSchedules, setAllBlockSchedules] = useState([]);
  const [showBlockScheduleForm, setShowBlockScheduleForm] = useState(false);
  const [editingBlockSchedule, setEditingBlockSchedule] = useState(null);
  const [newBlockSchedule, setNewBlockSchedule] = useState({
    doctor_id: null,
    block_from: '',
    block_until: '',
    reason: '',
    active: true
  });

  const fetchDoctors = async () => {
    try {
      const response = await calendarService.getDoctors();
      const doctorsList = response.items || [];
      const formattedDoctors = doctorsList.map(doctor => ({
        id: doctor.id,
        name: doctor.primary_name,
        speciality: doctor.speciality,
        service_provided: doctor.service_provided
      }));
      setDoctors(formattedDoctors);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctors([]);
    }
  };

  const fetchAllBlockSchedules = async () => {
    try {
      setScheduleLoading(true);
      const response = await calendarService.getAllBlockSchedules();
      setAllBlockSchedules(response.items || []);
    } catch (error) {
      console.error('Error fetching block schedules:', error);
      alert(t('calendar.loadBlockScheduleFailed'));
    } finally {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
    fetchAllBlockSchedules();
  }, []);

  const handleEditBlockSchedule = (blockSchedule) => {
    setEditingBlockSchedule(blockSchedule);
    setNewBlockSchedule({
      ...blockSchedule,
      doctor_id: blockSchedule.doctor_id
    });
    setShowBlockScheduleForm(true);
  };

  const handleDeleteBlockSchedule = async (blockSchedule) => {
    if (!window.confirm(t('calendar.confirmDeleteBlockSchedule'))) {
      return;
    }
    try {
      setActionLoading(true);
      await calendarService.deleteBlockSchedule(blockSchedule.id);
      alert(t('calendar.blockScheduleDeleted'));
      fetchAllBlockSchedules();
    } catch (error) {
      console.error('Error deleting block schedule:', error);
      alert(t('calendar.deleteBlockScheduleFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddBlockSchedule = () => {
    setNewBlockSchedule({
      doctor_id: null,
      block_from: '',
      block_until: '',
      reason: '',
      active: true
    });
    setEditingBlockSchedule(null);
    setShowBlockScheduleForm(true);
  };

  const handleBlockScheduleChange = (updatedBlockSchedule) => {
    setNewBlockSchedule(updatedBlockSchedule);
  };

  const handleCloseBlockScheduleForm = () => {
    setShowBlockScheduleForm(false);
    setEditingBlockSchedule(null);
    setNewBlockSchedule({
      doctor_id: null,
      block_from: '',
      block_until: '',
      reason: '',
      active: true
    });
  };

  const handleBlockScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);

      if (!newBlockSchedule.reason) {
        alert(t('calendar.pleaseEnterReason'));
        return;
      }
      if (!newBlockSchedule.block_from || !newBlockSchedule.block_until) {
        alert(t('calendar.pleaseSelectDateTimeRange'));
        return;
      }

      let blockFrom = newBlockSchedule.block_from;
      let blockUntil = newBlockSchedule.block_until;
      if (String(blockFrom).length === 10) blockFrom = `${blockFrom}T00:00:00`;
      if (String(blockUntil).length === 10) blockUntil = `${blockUntil}T23:59:59`;

      const blockScheduleData = {
        doctor_id: newBlockSchedule.doctor_id,
        block_from: blockFrom,
        block_until: blockUntil,
        reason: newBlockSchedule.reason,
        active: newBlockSchedule.active
      };

      if (editingBlockSchedule) {
        await calendarService.updateBlockSchedule(editingBlockSchedule.id, blockScheduleData);
        alert(t('calendar.blockScheduleUpdated'));
      } else {
        await calendarService.createBlockSchedule(blockScheduleData);
        alert(t('calendar.blockScheduleCreated'));
      }

      fetchAllBlockSchedules();
      setShowBlockScheduleForm(false);
      setEditingBlockSchedule(null);
    } catch (error) {
      console.error('Error saving block schedule:', error);
      alert(t('calendar.saveBlockScheduleFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-800">
          {t('calendar.blockScheduleManagement')}
        </h2>
        <button
          type="button"
          onClick={handleAddBlockSchedule}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white font-medium rounded-lg shadow-sm hover:bg-amber-600 hover:shadow focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all duration-200"
        >
          <PlusIcon className="h-5 w-5" />
          {t('calendar.addBlockSchedule')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <BlockScheduleList
          blockSchedules={allBlockSchedules}
          onEditBlockSchedule={handleEditBlockSchedule}
          onDeleteBlockSchedule={handleDeleteBlockSchedule}
          isLoading={scheduleLoading}
        />
      </div>

      <BlockScheduleDialog
        isOpen={showBlockScheduleForm}
        onClose={handleCloseBlockScheduleForm}
        editingBlockSchedule={editingBlockSchedule}
        blockScheduleForm={newBlockSchedule}
        onBlockScheduleChange={handleBlockScheduleChange}
        onSubmit={handleBlockScheduleSubmit}
        doctors={doctors}
        isSaving={actionLoading}
      />
    </div>
  );
};

export default HolidaySection;
